/** @module @liquidefy/code-editor/terminal/terminal-pty @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  PTYState,
  PTYConfig,
  PTYStatus,
  PTYSignal,
  PTYPlatform,
  PTYProcessId,
  ShellType,
  TerminalColor,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asPTYProcessId,
  clamp,
  DEFAULT_PTY_CONFIG,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 PTY CREATION — createPTY, configurePTY, defaultPTYConfig, validatePTYConfig
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new PTY state with defaults.
 */
export function createPTY(id: string, config: PTYConfig): EditorResult<PTYState> {
  const validation = validatePTYConfig(config);
  if (!validation.success) {
    return validation as EditorResult<PTYState>;
  }
  const state: PTYState = {
    processId: asPTYProcessId(id),
    pid: 0,
    shell: config.shell,
    cwd: config.cwd,
    cols: config.cols,
    rows: config.rows,
    status: 'starting' as PTYStatus,
    resourceUsage: {
      cpuPercent: 0,
      memoryBytes: 0,
      openFiles: 0,
    },
  };
  return ok(state, 'PTY_CREATED', `PTY created with shell ${config.shell}`);
}

/**
 * Build a PTYConfig from a shell type and optional overrides.
 */
export function configurePTY(shell: ShellType, overrides?: Partial<PTYConfig>): PTYConfig {
  const base = defaultPTYConfig(shell);
  if (!overrides) return base;
  return {
    shell: overrides.shell ?? base.shell,
    args: overrides.args ?? base.args,
    env: overrides.env ? { ...base.env, ...overrides.env } : base.env,
    cwd: overrides.cwd ?? base.cwd,
    cols: overrides.cols ?? base.cols,
    rows: overrides.rows ?? base.rows,
    encoding: overrides.encoding ?? base.encoding,
    handleFlowControl: overrides.handleFlowControl ?? base.handleFlowControl,
  };
}

/**
 * Return the default PTYConfig for a given shell.
 */
export function defaultPTYConfig(shell: ShellType): PTYConfig {
  return {
    ...DEFAULT_PTY_CONFIG,
    shell,
    args: defaultShellArgs(shell),
  };
}

/**
 * Default args for each known shell.
 */
function defaultShellArgs(shell: ShellType): string[] {
  switch (shell) {
    case 'bash': return ['--login'];
    case 'zsh': return ['--login', '--interactive'];
    case 'fish': return ['--interactive'];
    case 'powershell': return ['-NoLogo'];
    case 'pwsh': return ['-NoLogo'];
    case 'cmd': return ['/Q'];
    case 'nu': return [];
    case 'nushell': return [];
    case 'elvish': return [];
    case 'xonsh': return [];
    case 'ion': return [];
    case 'oil': return [];
    case 'murex': return [];
    case 'hilbish': return [];
    case 'sh': return [];
    case 'dash': return [];
    case 'ksh': return ['-l'];
    case 'csh': return ['-l'];
    case 'tcsh': return ['-l'];
    case 'custom': return [];
  }
}

/**
 * Validate that a PTYConfig has valid cols, rows, and encoding.
 */
export function validatePTYConfig(config: PTYConfig): EditorResult<PTYConfig> {
  if (config.cols < MIN_COLS || config.cols > MAX_COLS) {
    return err('PTY_INVALID_COLS', `Columns must be between ${MIN_COLS} and ${MAX_COLS}, got ${config.cols}`);
  }
  if (config.rows < MIN_ROWS || config.rows > MAX_ROWS) {
    return err('PTY_INVALID_ROWS', `Rows must be between ${MIN_ROWS} and ${MAX_ROWS}, got ${config.rows}`);
  }
  const validEncodings = ['utf-8', 'utf-16', 'ascii', 'latin1', 'iso-8859-1', 'utf8'];
  if (!validEncodings.includes(config.encoding.toLowerCase())) {
    return err('PTY_INVALID_ENCODING', `Unsupported encoding: ${config.encoding}`);
  }
  return ok(config, 'PTY_CONFIG_VALID', 'PTY configuration is valid');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 PTY LIFECYCLE — transitionPTY, state machine, start/suspend/resume/kill/exit
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Valid PTY status transitions as adjacency map.
 */
export const VALID_PTY_TRANSITIONS: Record<PTYStatus, PTYStatus[]> = {
  'starting': ['running', 'error', 'killed'],
  'running': ['suspended', 'exited', 'killed', 'error'],
  'suspended': ['running', 'killed', 'exited'],
  'exited': ['starting'],
  'killed': ['starting'],
  'error': ['starting', 'killed'],
};

/**
 * Transition PTY to a new status with validation.
 */
export function transitionPTY(state: PTYState, newStatus: PTYStatus): EditorResult<PTYState> {
  const valid = VALID_PTY_TRANSITIONS[state.status];
  if (!valid || !valid.includes(newStatus)) {
    return err(
      'PTY_INVALID_TRANSITION',
      `Cannot transition PTY from '${state.status}' to '${newStatus}'`
    );
  }
  const updated: PTYState = { ...state, status: newStatus };
  return ok(updated, 'PTY_TRANSITIONED', `PTY transitioned from ${state.status} to ${newStatus}`);
}

/**
 * Transition PTY to 'running'.
 */
export function startPTY(state: PTYState): EditorResult<PTYState> {
  return transitionPTY(state, 'running');
}

/**
 * Transition PTY to 'suspended'.
 */
export function suspendPTY(state: PTYState): EditorResult<PTYState> {
  return transitionPTY(state, 'suspended');
}

/**
 * Resume a suspended PTY to 'running'.
 */
export function resumePTY(state: PTYState): EditorResult<PTYState> {
  if (state.status !== 'suspended') {
    return err('PTY_NOT_SUSPENDED', `Cannot resume PTY in '${state.status}' state`);
  }
  return transitionPTY(state, 'running');
}

/**
 * Kill a PTY with a given signal.
 */
export function killPTY(state: PTYState, _signal: PTYSignal): EditorResult<PTYState> {
  if (state.status === 'exited' || state.status === 'killed') {
    return err('PTY_ALREADY_DEAD', `PTY is already in '${state.status}' state`);
  }
  return transitionPTY(state, 'killed');
}

/**
 * Mark PTY as exited with a given exit code.
 */
export function exitPTY(state: PTYState, _exitCode: number): EditorResult<PTYState> {
  return transitionPTY(state, 'exited');
}

/**
 * Check if PTY is currently running.
 */
export function isPTYRunning(state: PTYState): boolean {
  return state.status === 'running';
}

/**
 * Check if PTY is alive (running or suspended).
 */
export function isPTYAlive(state: PTYState): boolean {
  return state.status === 'running' || state.status === 'suspended';
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 PTY RESIZE — resizePTY, computeResize, fitToContainer, bounds
// ═══════════════════════════════════════════════════════════════════════════════

export const MIN_COLS = 2;
export const MAX_COLS = 1000;
export const MIN_ROWS = 1;
export const MAX_ROWS = 500;

/**
 * Resize PTY to given cols/rows, clamped to valid bounds.
 */
export function resizePTY(state: PTYState, cols: number, rows: number): EditorResult<PTYState> {
  const clampedCols = clamp(cols, MIN_COLS, MAX_COLS);
  const clampedRows = clamp(rows, MIN_ROWS, MAX_ROWS);

  if (clampedCols === state.cols && clampedRows === state.rows) {
    return ok(state, 'PTY_SIZE_UNCHANGED', 'PTY size unchanged');
  }

  const updated: PTYState = {
    ...state,
    cols: clampedCols,
    rows: clampedRows,
  };
  return ok(updated, 'PTY_RESIZED', `PTY resized to ${clampedCols}x${clampedRows}`);
}

/**
 * Compute new cols/rows from current size + deltas.
 */
export function computeResize(
  currentCols: number,
  currentRows: number,
  delta: { cols?: number; rows?: number }
): { cols: number; rows: number } {
  const newCols = clamp((currentCols + (delta.cols ?? 0)), MIN_COLS, MAX_COLS);
  const newRows = clamp((currentRows + (delta.rows ?? 0)), MIN_ROWS, MAX_ROWS);
  return { cols: newCols, rows: newRows };
}

/**
 * Compute cols/rows that fit within a container of given pixel dimensions.
 */
export function fitToContainer(
  containerWidth: number,
  containerHeight: number,
  charWidth: number,
  lineHeight: number
): { cols: number; rows: number } {
  if (charWidth <= 0 || lineHeight <= 0) {
    return { cols: MIN_COLS, rows: MIN_ROWS };
  }
  const rawCols = Math.floor(containerWidth / charWidth);
  const rawRows = Math.floor(containerHeight / lineHeight);
  return {
    cols: clamp(rawCols, MIN_COLS, MAX_COLS),
    rows: clamp(rawRows, MIN_ROWS, MAX_ROWS),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 SHELL DETECTION — detectShell, shellExecutable, shellDisplayName, features
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Detect a ShellType from a file path.
 */
export function detectShell(path: string): ShellType {
  const lower = path.toLowerCase();
  const basename = lower.split('/').pop()?.split('\\').pop() ?? '';
  const name = basename.replace(/\.exe$/i, '');

  const shellMap: Record<string, ShellType> = {
    'bash': 'bash',
    'zsh': 'zsh',
    'fish': 'fish',
    'powershell': 'powershell',
    'pwsh': 'pwsh',
    'cmd': 'cmd',
    'sh': 'sh',
    'dash': 'dash',
    'ksh': 'ksh',
    'csh': 'csh',
    'tcsh': 'tcsh',
    'nu': 'nu',
    'nushell': 'nushell',
    'elvish': 'elvish',
    'xonsh': 'xonsh',
    'ion': 'ion',
    'oil': 'oil',
    'osh': 'oil',
    'murex': 'murex',
    'hilbish': 'hilbish',
  };

  return shellMap[name] ?? 'custom';
}

/**
 * Return executable path for a shell type.
 */
export function shellExecutable(shell: ShellType): string {
  const map: Record<ShellType, string> = {
    'bash': '/bin/bash',
    'zsh': '/bin/zsh',
    'fish': '/usr/bin/fish',
    'powershell': 'powershell.exe',
    'pwsh': 'pwsh',
    'cmd': 'cmd.exe',
    'sh': '/bin/sh',
    'dash': '/bin/dash',
    'ksh': '/bin/ksh',
    'csh': '/bin/csh',
    'tcsh': '/bin/tcsh',
    'nu': 'nu',
    'nushell': 'nu',
    'elvish': 'elvish',
    'xonsh': 'xonsh',
    'ion': 'ion',
    'oil': 'osh',
    'murex': 'murex',
    'hilbish': 'hilbish',
    'custom': '/bin/sh',
  };
  return map[shell];
}

/**
 * Return a human-readable display name for a shell type.
 */
export function shellDisplayName(shell: ShellType): string {
  const names: Record<ShellType, string> = {
    'bash': 'Bash',
    'zsh': 'Zsh',
    'fish': 'Fish',
    'powershell': 'PowerShell',
    'pwsh': 'PowerShell Core',
    'cmd': 'Command Prompt',
    'sh': 'Bourne Shell',
    'dash': 'Dash',
    'ksh': 'Korn Shell',
    'csh': 'C Shell',
    'tcsh': 'TENEX C Shell',
    'nu': 'Nushell',
    'nushell': 'Nushell',
    'elvish': 'Elvish',
    'xonsh': 'Xonsh',
    'ion': 'Ion',
    'oil': 'Oil Shell',
    'murex': 'Murex',
    'hilbish': 'Hilbish',
    'custom': 'Custom Shell',
  };
  return names[shell];
}

/**
 * Return the default shell for a given platform.
 */
export function defaultShellForPlatform(platform: PTYPlatform): ShellType {
  switch (platform) {
    case 'darwin': return 'zsh';
    case 'linux': return 'bash';
    case 'win32': return 'powershell';
    case 'freebsd': return 'sh';
    case 'openbsd': return 'sh';
  }
}

/**
 * Check if a shell is typically available on a given platform.
 */
export function isShellAvailable(shell: ShellType, platform: PTYPlatform): boolean {
  const winOnly: ShellType[] = ['cmd', 'powershell'];
  const unixOnly: ShellType[] = ['bash', 'zsh', 'fish', 'sh', 'dash', 'ksh', 'csh', 'tcsh'];
  const crossPlatform: ShellType[] = ['pwsh', 'nu', 'nushell', 'elvish', 'xonsh', 'ion', 'oil', 'murex', 'hilbish', 'custom'];

  if (platform === 'win32') {
    return winOnly.includes(shell) || crossPlatform.includes(shell);
  }
  return unixOnly.includes(shell) || crossPlatform.includes(shell);
}

/**
 * Return feature support flags for a shell.
 */
export function shellFeatures(shell: ShellType): {
  supportsUnicode: boolean;
  supportsColor: boolean;
  supports256Color: boolean;
  supportsTrueColor: boolean;
  supportsMouseReporting: boolean;
} {
  const fullColor: ShellType[] = ['bash', 'zsh', 'fish', 'pwsh', 'nu', 'nushell', 'elvish', 'xonsh', 'hilbish'];
  const basicColor: ShellType[] = ['sh', 'dash', 'ksh', 'csh', 'tcsh', 'cmd', 'powershell', 'ion', 'oil', 'murex'];

  const is256 = fullColor.includes(shell);
  const isTrue = fullColor.includes(shell);
  const hasColor = fullColor.includes(shell) || basicColor.includes(shell);
  const hasUnicode = shell !== 'cmd';
  const hasMouse = ['zsh', 'bash', 'fish', 'elvish'].includes(shell);

  return {
    supportsUnicode: hasUnicode,
    supportsColor: hasColor,
    supports256Color: is256,
    supportsTrueColor: isTrue,
    supportsMouseReporting: hasMouse,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 PTY ENVIRONMENT — buildPTYEnvironment, sanitizeEnvironment, TERM values
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Variables that should never be inherited from the host environment.
 */
export const DANGEROUS_ENV_VARS: string[] = [
  'LD_PRELOAD',
  'LD_LIBRARY_PATH',
  'DYLD_INSERT_LIBRARIES',
  'DYLD_LIBRARY_PATH',
  'DYLD_FRAMEWORK_PATH',
  'NODE_OPTIONS',
  'ELECTRON_RUN_AS_NODE',
  'PYTHONPATH',
  'PERL5LIB',
  'RUBYLIB',
  'CLASSPATH',
  'BASH_ENV',
  'ENV',
  'CDPATH',
  'GLOBIGNORE',
  'PROMPT_COMMAND',
  'PS4',
  'SHELLOPTS',
  'BASHOPTS',
];

/**
 * Per-shell required environment variables.
 */
export const SHELL_ENV_VARS: Record<string, Record<string, string>> = {
  bash: {
    BASH_SILENCE_DEPRECATION_WARNING: '1',
  },
  zsh: {
    ZSH_DISABLE_COMPFIX: 'true',
  },
  fish: {
    fish_greeting: '',
  },
  powershell: {},
  pwsh: {
    DOTNET_CLI_TELEMETRY_OPTOUT: '1',
  },
  nu: {},
  nushell: {},
  elvish: {},
  xonsh: {},
  ion: {},
  oil: {},
  sh: {},
  dash: {},
  ksh: {},
  csh: {},
  tcsh: {},
  cmd: {},
  murex: {},
  hilbish: {},
  custom: {},
};

/**
 * Mapping from color support level to $TERM value.
 */
export const TERM_VALUES: Record<string, string> = {
  'none': 'dumb',
  'basic': 'xterm',
  '256': 'xterm-256color',
  'truecolor': 'xterm-256color',
  'kitty': 'xterm-kitty',
};

/**
 * Build the environment for a PTY by merging base, shell-specific, and overrides.
 * Removes dangerous vars from the base.
 */
export function buildPTYEnvironment(
  base: Record<string, string>,
  overrides: Record<string, string>,
  shell: ShellType
): Record<string, string> {
  const sanitized = sanitizeEnvironment(base);
  const shellVars = SHELL_ENV_VARS[shell] ?? {};
  return { ...sanitized, ...shellVars, ...overrides };
}

/**
 * Remove dangerous environment variables from the env map.
 */
export function sanitizeEnvironment(env: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {};
  const dangerousSet = new Set(DANGEROUS_ENV_VARS);
  for (const key of Object.keys(env)) {
    if (!dangerousSet.has(key)) {
      result[key] = env[key]!;
    }
  }
  return result;
}

/**
 * Set the TERM and related env vars based on color support level.
 */
export function setTermInfo(
  env: Record<string, string>,
  colorSupport: 'none' | 'basic' | '256' | 'truecolor'
): Record<string, string> {
  const term = TERM_VALUES[colorSupport] ?? TERM_VALUES['256']!;
  const base: Record<string, string> = { ...env, TERM: term };

  if (colorSupport === 'truecolor') {
    return { ...base, COLORTERM: 'truecolor' };
  } else if (colorSupport === '256') {
    return { ...base, COLORTERM: '256color' };
  } else {
    const result: Record<string, string> = {};
    for (const key of Object.keys(base)) {
      if (key !== 'COLORTERM') {
        result[key] = base[key] as string;
      }
    }
    return result;
  }
}
